import React from "react";
import { LifeBuoy, AlertTriangle, MessageSquare, Bug } from "lucide-react";
import { Link } from "react-router-dom";
import { Chip, Stat, Loading, Empty, SectionHeader, timeAgo, LoadError, usePlatformFetch } from "./_primitives";

export default function SupportCenter() {
  const { data, error, retry } = usePlatformFetch("/platform/support");
  if (error) return <LoadError error={error} retry={retry} label="Support" />;
  if (!data) return <Loading />;
  const s = data.summary || {};
  return (
    <div className="space-y-4" data-testid="support-center">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Open discoveries" value={s.open_discoveries ?? 0} accent={s.open_discoveries ? "amber" : undefined} />
        <Stat label="Open feedback" value={s.open_feedback ?? 0} accent={s.open_feedback ? "amber" : undefined} />
        <Stat label="P0/P1 tickets" value={s.p0_p1_count ?? 0} accent={s.p0_p1_count ? "red" : undefined} />
        <Stat label="Incidents · 72h" value={s.incidents_72h ?? 0} accent={s.incidents_72h > 10 ? "amber" : undefined} sub="error+critical audit events" />
      </div>

      <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="support-discoveries">
        <SectionHeader icon={<MessageSquare size={11} />} title={`Discoveries (${(data.discoveries || []).length})`} />
        {(data.discoveries || []).length === 0 ? <Empty>No open discoveries.</Empty> : (
          <table className="w-full text-xs">
            <thead className="bg-[var(--fop-surface)] text-[var(--fop-text-3)]">
              <tr><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Tenant</th><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Title</th><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Priority</th><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Author</th><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Created</th></tr>
            </thead>
            <tbody>
              {(data.discoveries || []).slice(0, 30).map((d) => (
                <tr key={d.id} className="border-t border-[var(--fop-border)]" data-testid={`discovery-row-${d.id}`}>
                  <td className="px-3 py-2"><Link to={`/platform/tenants/${d.tenant_id}`} className="hover:text-[var(--fop-primary)]">{d.tenant_name}</Link></td>
                  <td className="px-3 py-2 text-[var(--fop-text-2)]">{d.title || "—"}</td>
                  <td className="px-3 py-2"><Chip state={(d.priority || "p2").toLowerCase()} label={(d.priority || "p2").toUpperCase()} /></td>
                  <td className="px-3 py-2 font-mono text-[10px] text-[var(--fop-text-4)]">{d.created_by_name || d.user_role || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-[var(--fop-text-4)]">{timeAgo(d.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="support-incidents">
        <SectionHeader icon={<AlertTriangle size={11} />} title={`Incidents · 72h (${(data.incidents || []).length})`} />
        {(data.incidents || []).length === 0 ? <Empty>No incidents in last 72h.</Empty> : (
          <table className="w-full text-xs">
            <tbody>
              {(data.incidents || []).slice(0, 25).map((i) => (
                <tr key={i.id} className="border-t border-[var(--fop-border)]" data-testid={`incident-row-${i.id}`}>
                  <td className="px-3 py-1.5 w-1/4"><Link to={`/platform/tenants/${i.tenant_id}`} className="hover:text-[var(--fop-primary)] text-[11px]">{i.tenant_name}</Link></td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-[var(--fop-text-2)]">{i.action}</td>
                  <td className="px-3 py-1.5"><Chip state={i.severity === "critical" ? "red" : "amber"} label={i.severity} /></td>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-[var(--fop-text-4)] text-right">{timeAgo(i.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="support-feedback">
        <SectionHeader icon={<Bug size={11} />} title={`Feedback (${(data.feedback || []).length})`} />
        {(data.feedback || []).length === 0 ? <Empty>No open feedback rows.</Empty> : (
          <table className="w-full text-xs">
            <tbody>
              {(data.feedback || []).slice(0, 25).map((f) => (
                <tr key={f.id} className="border-t border-[var(--fop-border)]" data-testid={`feedback-row-${f.id}`}>
                  <td className="px-3 py-1.5"><Link to={`/platform/tenants/${f.tenant_id}`} className="hover:text-[var(--fop-primary)] text-[11px]">{f.tenant_name}</Link></td>
                  <td className="px-3 py-1.5 text-[var(--fop-text-2)]">{f.title}</td>
                  <td className="px-3 py-1.5"><Chip state={(f.category || "p2").toLowerCase()} label={f.category} /></td>
                  <td className="px-3 py-1.5 text-[10px] font-mono text-[var(--fop-text-4)]">{f.status}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-[var(--fop-text-4)] text-right">{timeAgo(f.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
